import type { TravelMode } from './travel';

/** The one place the travel-block colors are defined. */
export const CONFLICT = '#ef3d23';

/**
 * Travel blocks carry their own palette, distinct from the conflict red. The
 * fill sits at 50% so the sky and the hour rules stay legible behind it.
 */
export const TRAVEL_FILL = 'rgba(173, 111, 124, 0.5)';
export const TRAVEL_STROKE = '#752436';

/**
 * Every travel block uses the same palette, whether or not the connection is
 * makeable. A block that cannot be made is marked by the ef3d23 warning glyph
 * and the "N min discrepancy" reading, not by a different fill — so the color
 * stays a consistent signal for "you are moving here".
 */
export const travelFill = () => TRAVEL_FILL;
export const travelBorder = () => TRAVEL_STROKE;

/**
 * Doubled from the original: 2px stroke, and a dash pattern twice as long and
 * twice as widely spaced. CSS `dashed` derives its dash length from the border
 * width, so doubling the width doubles the dashes for free.
 */
export const TRAVEL_BORDER_WIDTH = 2;

/** Google Maps travel modes, keyed by our own estimate's mode. */
const GMAPS_MODE: Record<TravelMode, string> = {
  walk: 'walking',
  bike: 'bicycling',
  transit: 'transit',
  ferry: 'transit',
};

/**
 * Directions between two venues, in the given mode.
 *
 * Either end may be a neighborhood rather than an address — plenty of listings
 * publish no street address, and "Manhattan, New York" is a place Google can
 * still route to. Returning null for those meant the one journey most in need
 * of a real map got no link at all.
 */
export function directionsUrl(
  from: string | null,
  to: string | null,
  mode: TravelMode,
): string | null {
  if (!from || !to) return null;
  return (
    'https://www.google.com/maps/dir/?api=1' +
    `&origin=${encodeURIComponent(from)}` +
    `&destination=${encodeURIComponent(to)}` +
    `&travelmode=${GMAPS_MODE[mode]}`
  );
}
